import React, { useState } from "react";

import { HyperLinkRedirectControl } from "@/Common/Components/Buttons/HyperLinkRedirectControl";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { PortalStatusIcon } from "@/Common/Components/Portal/PortalStatusIcon";
import { MyleTableColumn, MyleTableColumnInit, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { LuxonDateHelper } from "@/Common/Helpers/LuxonDateHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { MyleTableHelper } from "@/Common/Helpers/MyleTableHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { SharedItemCategory } from "@/Common/Models/Portal/SharedItem/SharedItemCategory";
import { SharedItemPriority } from "@/Common/Models/Portal/SharedItem/SharedItemPriority";
import { PortalCommunicationComposite } from "@/Common/Models/PortalCommunication/PortalCommunicationComposite";
import { CommunicationsUIStateInbox, PortalCommunicationOrderType, RedirectTo, SortDirection } from "@/Common/Models/UI/Enums";
import { ColumnConfigurationModel } from "@/Common/Models/UI/Tables/ColumnConfigurationModel";
import { TableDataSourceItem } from "@/Common/Models/UI/Tables/ITableDataSourceItem";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { CommunicationsHelper } from "@/Communications/Helpers/CommunicationsHelper";
import { PortalCommunicationTableSetting } from "@/Communications/Models/PortalCommunication/PortalCommunicationTableSetting";

import FlagForReviewSvg from "@/Common/_images/flag-for-review.svg";

interface IUsePortalCommunicationInboxTableReturn {
    columns: Readonly<Immutable.List<MyleTableColumn>>;
    tableRenderOptions: Readonly<ITableRenderOptions>;
    columnConfigurationModels: ColumnConfigurationModel[];
    saveOptionKey: string;
}

const CommonTableColumns = {
    Checkbox: "Checkbox",
    Subject: "Subject",
    Patient: "Patient",
    To: "To",
    From: "From",
    Priority: "Priority",
    Action: "Action"
} as const;

const InboxTableColumn = {
    ReceivedDate: "ReceivedDate",
    Category: "Category",
    NextAppointment: "NextAppointment"
} as const;

const SentTableColumn = {
    Date: "Date",
    Portal: "Portal"
} as const;

export function usePortalCommunicationTable(
    setting: PortalCommunicationTableSetting,
    inboxType: CommunicationsUIStateInbox,
    sharedItemCategories: SharedItemCategory[],
    toggleIsSelectedBySharedItemId: (sharedItemId: string) => void,
    toggleAllIsSelected: (isChecked: boolean) => void,
    updateOrderTypeAndDirection: (newOrderType: PortalCommunicationOrderType, newOrderDirection: SortDirection) => void,
    openNotesPopup: (e: React.MouseEvent, sharedItemId: string, totalNotes: number) => void
): IUsePortalCommunicationInboxTableReturn {
    const { getText } = useTranslation();
    const [isCheckAllChecked, setIsCheckAllChecked] = useState(false);
    const [orderType, setOrderType] = useState(setting.queryOrderType);
    const [orderDirection, setOrderDirection] = useState(setting.queryOrderDirection);

    function getSaveOptionKeyByInboxType(inboxType: CommunicationsUIStateInbox): "PortalCommunicationInboxTableSetting" | "PortalCommunicationSentTableSetting" {
        if (CommunicationsHelper.isInboxTypeSent(inboxType)) {
            return "PortalCommunicationSentTableSetting";
        }

        return "PortalCommunicationInboxTableSetting";
    }

    function validateReadSharedItem(sharedItemDateReadByUser: string, latestPatientSharedItemMessageDateCreated: string): boolean {
        if (!latestPatientSharedItemMessageDateCreated) {
            return true;
        }

        if (!sharedItemDateReadByUser) {
            return false;
        }

        const sharedItemDateReadByUserDate = new Date(sharedItemDateReadByUser);
        const latestPatientSharedItemMessageDateCreatedDate = new Date(latestPatientSharedItemMessageDateCreated);

        return latestPatientSharedItemMessageDateCreatedDate < sharedItemDateReadByUserDate;
    }

    function getTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        const buildTableColumnsInit = (column: MyleTableColumnOptions): MyleTableColumnInit<TableDataSourceItem<PortalCommunicationComposite>> => ({
            renderCell: (item: TableDataSourceItem<PortalCommunicationComposite>) => {
                const rowItem = formatCellText(column.FieldName, item);

                if (column.FieldName === SentTableColumn.Portal) {
                    return rowItem;
                }

                const markAsUnreadCssClass = validateReadSharedItem(
                    item.Model.sharedItem?.DateReadByUser?.toString(),
                    item.Model.latestPatientSharedItemMessage?.DateCreated?.toString()
                ) ? "" : "bold";
                return <div className={markAsUnreadCssClass}>{rowItem}</div>;
            },
            ...(column.FieldName === CommonTableColumns.Checkbox && inboxType !== CommunicationsUIStateInbox.Sent && { renderHeader: () => (
                <div>
                    <CheckboxControl
                        onChange={isChecked => {
                            setIsCheckAllChecked(isChecked);
                            toggleAllIsSelected(isChecked);
                        }}
                        checked={isCheckAllChecked}
                    />
                </div>
            ) })
        });

        return MyleTableColumn.buildList<TableDataSourceItem<PortalCommunicationComposite>>(
            columnsOptions,
            column => buildTableColumnsInit(column),
            false
        );
    }

    function formatCellText(fieldName: string, item: TableDataSourceItem<PortalCommunicationComposite>): JSX.Element {
        let latestSharedItemMessage = item.Model.getLatestSharedItemMessage();

        if (inboxType === CommunicationsUIStateInbox.Inbox) {
            // In inbox, it's supposed to show the latest message sent by patient
            latestSharedItemMessage = item.Model.latestPatientSharedItemMessage;
        }
        else if (inboxType === CommunicationsUIStateInbox.Sent) {
            // In sent, it's supposed to show the latest message sent by employee
            latestSharedItemMessage = item.Model.latestEmployeeSharedItemMessage;
        }

        switch (fieldName) {
            case CommonTableColumns.Checkbox:
            {
                if (inboxType === CommunicationsUIStateInbox.Sent || !item.Model.sharedItem?.EmployeeContactId || item.Model.sharedItem?.EmployeeContactId !== sessionStore.getCurrentEmployee()?.ContactId) {
                    return null;
                }

                return (
                    <CheckboxControl
                        id={item.Model.sharedItem.SharedItemId}
                        onChange={() => toggleIsSelectedBySharedItemId(item.Model.sharedItem.SharedItemId)}
                        checked={item.IsSelected}
                    />
                );
            }
            case CommonTableColumns.Subject:
                return (
                    <div>
                        {item.Model.subjectWithNumberOfMessages}
                    </div>
                );
            case CommonTableColumns.Patient:
                return (
                    <div>
                        <HyperLinkRedirectControl
                            target={RedirectTo.Patient}
                            redirectValue={GuidHelper.AsGuid(item.Model.sharedItem.PatientContactId)}
                            text={item.Model.capitalizedPatientName}
                        />
                    </div>
                );
            case CommonTableColumns.To:
                return <div>{item.Model.getToName(inboxType)}</div>;
            case CommonTableColumns.From:
                return <div>{item.Model.getFromName(inboxType)}</div>;
            case CommonTableColumns.Priority:
            {
                const priority = latestSharedItemMessage?.Priority;

                if (IsNullOrUndefined(priority)) {
                    return null;
                }

                return (
                    <div>
                        {getText(`Enums_SharedItemPriority_${EnumHelper.getName(SharedItemPriority, priority)}`)}
                    </div>
                );
            }
            case CommonTableColumns.Action:
                return (
                    <div className="inline-flex-center margin-sm-left">
                        <a className="margin-xm-right" style={{ minWidth: 35 }}onClick={e => openNotesPopup(e, item.Model.sharedItem.SharedItemId, item.Model.numberOfSharedItemNotes)}>[
                            {item.Model.numberOfSharedItemNotes > 0 ?
                                <span className="red-text">{` ${item.Model.numberOfSharedItemNotes} `}</span> :
                                <span> + </span>
                            }
                            ]
                        </a>
                        {item.Model.sharedItem.IsFlaggedForReview && (
                            <FlagForReviewSvg />
                        )}
                    </div>
                );
            case InboxTableColumn.ReceivedDate:
            case SentTableColumn.Date:
            {
                const dateCreated = latestSharedItemMessage?.DateCreated;
                return <div>{LuxonDateHelper.formatToFullDateTime(dateCreated, "/")}</div>;
            }
            case InboxTableColumn.Category:
            {
                const categoryId = item.Model.sharedItem.SharedItemCategoryId;

                if (!categoryId)
                    return <div>{getText("SharedItem_SharedItemCategory_ClinicInitiated")}</div>;

                const category = sharedItemCategories.find(p => p.Id === categoryId);

                return <div>{cacheStore.GetLocalizedValueByLookupId(category.NameLookupId)}</div>;
            }
            case InboxTableColumn.NextAppointment:
                return <span>{item.Model.parsedPatientNextAppointment}</span>;
            case SentTableColumn.Portal:
                return (
                    <PortalStatusIcon
                        {...PortalHelper.getPortalButtonPropsForSyncStatus(
                            latestSharedItemMessage?.Status ?? item.Model.sharedItem.Status,
                            item.Model.sharedItem.IncludeLinkedItem
                        )}
                    />
                );
            default:
                return null;
        }
    }

    function getCommonTableColumnOptions(options: {
        headerText: string,
        fieldName: string,
        widthPercentage: number,
        sortColumn?: PortalCommunicationOrderType,
        isRowClickable?: boolean,
        isSortable?: boolean,
        isFixedInPlace?: boolean
    }): MyleTableColumnOptions {
        const sortTable = (newOrderType: PortalCommunicationOrderType): void => {
            const isOrderTypeDifferent = orderType !== newOrderType;
            if (isOrderTypeDifferent) {
                setOrderType(newOrderType);
            }

            const newOrderDirection = MyleTableHelper.getNewSortDir(newOrderType, orderType, orderDirection);
            const isOrderDirectionDifferent = orderDirection !== newOrderDirection;
            if (isOrderDirectionDifferent) {
                setOrderDirection(newOrderDirection);
            }

            if (isOrderTypeDifferent || isOrderDirectionDifferent) {
                updateOrderTypeAndDirection(newOrderType, newOrderDirection);
            }
        };

        return new MyleTableColumnOptions({
            HeaderText: options.headerText,
            FieldName: options.fieldName,
            RowClickEnabled: options.isRowClickable ?? true,
            IsSortable: options.isSortable ?? true,
            SortColumn: options.sortColumn,
            SortDirection: orderDirection,
            IsSortActive: orderType === options.sortColumn,
            WidthPercentageDefault: options.widthPercentage,
            IsFixedInPlace: options.isFixedInPlace ?? false,
            sortingMethod: () => sortTable(options.sortColumn)
        });
    }

    function getTableRenderOptions(columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        const defaultTablePluginOptions = {
            MinColWidth: 50,
            MinTableWidth: 950,
            Cols: columnOptions.filter(p => !p.IsHidden).map(p => p.WidthPercentage),
            FirstColumnNoDrag: false,
            LastColumnNoDrag: true,
            Autoresize: true,
            OnColumnWidthChange: null
        };

        return Immutable.fromJS(defaultTablePluginOptions);
    }

    const getMyleTableColumnOptions = (): MyleTableColumnOptions[] => {
        const commonTableColumnOptions = [
            getCommonTableColumnOptions({
                headerText: getText("SubjectLabel"),
                fieldName: CommonTableColumns.Subject,
                sortColumn: PortalCommunicationOrderType.Subject,
                widthPercentage: 10
            }),
            getCommonTableColumnOptions({
                headerText: getText("PatientLabel"),
                fieldName: CommonTableColumns.Patient,
                sortColumn: PortalCommunicationOrderType.Patient,
                widthPercentage: 10
            }),
            getCommonTableColumnOptions({
                headerText: getText("ToLabel"),
                fieldName: CommonTableColumns.To,
                widthPercentage: 10,
                isSortable: false
            }),
            getCommonTableColumnOptions({
                headerText: getText("FromLabel"),
                fieldName: CommonTableColumns.From,
                widthPercentage: 10,
                isSortable: false
            }),
            getCommonTableColumnOptions({
                headerText: getText("PriorityLabel"),
                fieldName: CommonTableColumns.Priority,
                sortColumn: PortalCommunicationOrderType.Priority,
                widthPercentage: 10
            }),
            getCommonTableColumnOptions({
                headerText: getText("ActionLabel"),
                fieldName: CommonTableColumns.Action,
                widthPercentage: 10,
                isSortable: false,
                isFixedInPlace: true,
                isRowClickable: false
            })
        ];

        if (CommunicationsHelper.isInboxTypeSent(inboxType)) {
            return [
                ...commonTableColumnOptions,
                getCommonTableColumnOptions({
                    headerText: getText("DateLabel"),
                    fieldName: SentTableColumn.Date,
                    sortColumn: PortalCommunicationOrderType.Date,
                    widthPercentage: 10
                }),
                getCommonTableColumnOptions({
                    headerText: getText("PortalLabel"),
                    fieldName: SentTableColumn.Portal,
                    widthPercentage: 15,
                    isSortable: false
                })
            ];
        }

        return [
            getCommonTableColumnOptions({
                headerText: "",
                fieldName: CommonTableColumns.Checkbox,
                widthPercentage: 5,
                isSortable: false,
                isRowClickable: false,
                isFixedInPlace: true
            }),
            ...commonTableColumnOptions,
            getCommonTableColumnOptions({
                headerText: getText("CategoryDLabel"),
                fieldName: InboxTableColumn.Category,
                widthPercentage: 10,
                isSortable: false
            }),
            getCommonTableColumnOptions({
                headerText: getText("FutureAPPTLabel"),
                fieldName: InboxTableColumn.NextAppointment,
                widthPercentage: 12,
                isSortable: false
            }),
            getCommonTableColumnOptions({
                headerText: getText("ReceivedDateLabel"),
                fieldName: InboxTableColumn.ReceivedDate,
                sortColumn: PortalCommunicationOrderType.Date,
                widthPercentage: 10
            })
        ];
    };

    function setTableColumnsOptionsByDefaultOrder(tableColumnOptions: Readonly<MyleTableColumnOptions[]>): MyleTableColumnOptions[] {
        const getWeightByFieldName = (fieldName: string): number => {
            switch (fieldName) {
                case CommonTableColumns.Checkbox:
                    return 0;
                case CommonTableColumns.Subject:
                    return 1;
                case CommonTableColumns.Patient:
                    return 2;
                case InboxTableColumn.ReceivedDate:
                case SentTableColumn.Date:
                    return 3;
                case CommonTableColumns.To:
                    return 4;
                case CommonTableColumns.From:
                    return 5;
                case InboxTableColumn.Category:
                    return 6;
                case CommonTableColumns.Priority:
                case SentTableColumn.Portal:
                    return 7;
                case InboxTableColumn.NextAppointment:
                    return 8;
                case CommonTableColumns.Action:
                    return 9;
                default:
                    return 10;
            }
        };

        return [...tableColumnOptions].sort(({ FieldName: columnOptionAFieldName }, { FieldName: columnOptionBFieldName }) =>
            getWeightByFieldName(columnOptionAFieldName) - getWeightByFieldName(columnOptionBFieldName)
        );
    }

    const saveOptionKey = getSaveOptionKeyByInboxType(inboxType);
    const columnsOptions = (() => {
        const myleTableColumnOptions = setTableColumnsOptionsByDefaultOrder(getMyleTableColumnOptions());
        return cacheStore.Helper.loadTableColumnOptionsFromSettings(saveOptionKey, myleTableColumnOptions);
    })();
    const columns = getTableColumns(columnsOptions);
    const tableRenderOptions = getTableRenderOptions(columnsOptions);
    const columnConfigurationModels = new MyleHelper().castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());

    return {
        columns,
        tableRenderOptions,
        columnConfigurationModels,
        saveOptionKey
    };
}
